/*
 * PKI.Pkcs12 — .pfx / .p12 bundles: read and write.
 *
 * This is the format people are stuck with (IIS, Tomcat, Azure, code signing)
 * and the one every browser-based converter gives up on, because opening a real
 * one needs three things WebCrypto does not have: the PKCS#12 KDF, RC2-40 and
 * 3DES. PKI.Ciphers supplies all three, so we can open the .pfx a colleague
 * exported from Windows in 2019 — which is the actual job.
 *
 * Writing offers two profiles, because there is no single right answer:
 *   modern     AES-256-CBC + PBKDF2-SHA256 + HMAC-SHA256  (OpenSSL 3, Win10+, Java 11+)
 *   compatible 3DES + RC2-40 + HMAC-SHA1                  (old Java, Android, appliances)
 * A bundle written in the modern profile silently fails to import on older
 * stacks with a generic "wrong password" error, so the choice has to be visible
 * rather than hidden behind a default.
 */
(function (root) {
    'use strict';

    var P = (root && root.PKI) || {};
    var Asn1 = P.Asn1 || (typeof require !== 'undefined' ? require('./asn1.js') : null);
    var Oid = P.Oid || (typeof require !== 'undefined' ? require('./oid.js') : null);
    var Keys = P.Keys || (typeof require !== 'undefined' ? require('./keys.js') : null);
    var Ciphers = P.Ciphers || (typeof require !== 'undefined' ? require('./ciphers.js') : null);

    var TAG = Asn1.TAG;
    var OID_DATA = '1.2.840.113549.1.7.1';
    var OID_ENCRYPTED_DATA = '1.2.840.113549.1.7.6';
    var OID_KEY_BAG = '1.2.840.113549.1.12.10.1.1';
    var OID_SHROUDED_KEY_BAG = '1.2.840.113549.1.12.10.1.2';
    var OID_CERT_BAG = '1.2.840.113549.1.12.10.1.3';
    var OID_X509_CERT = '1.2.840.113549.1.9.22.1';
    var OID_FRIENDLY_NAME = '1.2.840.113549.1.9.20';
    var OID_LOCAL_KEY_ID = '1.2.840.113549.1.9.21';
    var OID_PBE_3DES = '1.2.840.113549.1.12.1.3';
    var OID_PBE_RC2_40 = '1.2.840.113549.1.12.1.6';
    var OID_SHA1 = '1.3.14.3.2.26';
    var OID_SHA256 = '2.16.840.1.101.3.4.2.1';

    /* ═══ reading ═══════════════════════════════════════════════════ */

    /**
     * Open a PFX. Returns {certificates:[], keys:[], macVerified, integrity, warnings}.
     *
     * The MAC is checked first and separately from decryption, because it is the
     * only reliable "is this the right password" signal — decrypting with the
     * wrong password usually yields a padding error, but not always, and the
     * difference between "wrong password" and "corrupt file" matters to a user
     * staring at a bundle their colleague sent.
     */
    async function read(der, password) {
        var bytes = Asn1.u8(der);
        var node = Asn1.parse(bytes);
        if (!node.children || node.children.length < 2) throw new Error('PFX_MALFORMED');

        var version = node.children[0].bytes[0];
        var authSafeInfo = node.children[1];
        var macData = node.children[2] || null;

        var contentType = authSafeInfo.children[0].oid;
        if (contentType !== OID_DATA) throw new Error('PFX_UNSUPPORTED_OUTER:' + Oid.label(contentType));
        var authSafeBytes = unwrapOctets(authSafeInfo.children[1].children[0]);

        var result = {
            version: version,
            certificates: [], keys: [], warnings: [],
            macVerified: null, macAlgorithm: null, macIterations: null,
            encryptionSummary: []
        };

        // ── MAC first
        var effectivePassword = password;
        if (macData) {
            var mac = await verifyMac(macData, authSafeBytes, password);
            result.macVerified = mac.ok;
            result.macAlgorithm = mac.algorithm;
            result.macIterations = mac.iterations;
            if (!mac.ok && mac.okWithAlternateEmpty) {
                // OpenSSL and some Java versions disagree on how to encode an
                // empty password; try the other convention before giving up.
                effectivePassword = mac.alternatePassword;
                result.macVerified = true;
                result.warnings.push('EMPTY_PASSWORD_CONVENTION');
            }
            if (!result.macVerified) {
                var err = new Error('WRONG_PASSWORD');
                err.macFailed = true;
                throw err;
            }
        } else {
            result.warnings.push('NO_MAC');
        }

        // ── AuthenticatedSafe: a sequence of ContentInfo
        var safe = Asn1.parse(authSafeBytes);
        var contents = safe.children || [];

        for (var i = 0; i < contents.length; i++) {
            var ci = contents[i];
            var ctype = ci.children[0].oid;
            var safeContentsDer;

            if (ctype === OID_DATA) {
                safeContentsDer = unwrapOctets(ci.children[1].children[0]);
                result.encryptionSummary.push({ index: i, encrypted: false, algorithm: null });
            } else if (ctype === OID_ENCRYPTED_DATA) {
                var encData = ci.children[1].children[0];      // EncryptedData
                var eci = encData.children[1];                  // EncryptedContentInfo
                var algIdNode = eci.children[1];
                var encContent = findImplicitContent(eci);
                var algOid = algIdNode.children[0].oid;
                result.encryptionSummary.push({
                    index: i, encrypted: true,
                    algorithm: Oid.label(algOid), algorithmOid: algOid
                });
                try {
                    safeContentsDer = await Keys.decryptPbe(algIdNode, encContent, effectivePassword);
                } catch (e) {
                    if (String(e.message).indexOf('PBE_UNSUPPORTED') === 0) {
                        result.warnings.push('UNSUPPORTED_CERT_ENCRYPTION:' + Oid.label(algOid));
                        continue;
                    }
                    throw e;
                }
            } else {
                result.warnings.push('UNSUPPORTED_CONTENT:' + Oid.label(ctype));
                continue;
            }

            await readSafeContents(safeContentsDer, effectivePassword, result);
        }

        // Pair keys to certificates by localKeyId, then by public-key equality.
        await pairKeysAndCerts(result);
        return result;
    }

    async function readSafeContents(der, password, result) {
        var contents = Asn1.parse(der);
        var bags = contents.children || [];
        for (var i = 0; i < bags.length; i++) {
            var bag = bags[i];
            if (!bag.children || bag.children.length < 2) continue;
            var bagId = bag.children[0].oid;
            var bagValue = bag.children[1];               // [0] EXPLICIT
            var attrs = readBagAttributes(bag.children[2]);

            if (bagId === OID_CERT_BAG) {
                var certBag = bagValue.children[0];
                if (!certBag || !certBag.children) continue;
                if (certBag.children[0].oid !== OID_X509_CERT) {
                    result.warnings.push('UNSUPPORTED_CERT_TYPE:' + Oid.label(certBag.children[0].oid));
                    continue;
                }
                var certDer = unwrapOctets(certBag.children[1].children[0]);
                result.certificates.push({
                    der: certDer,
                    friendlyName: attrs.friendlyName,
                    localKeyId: attrs.localKeyId
                });
            } else if (bagId === OID_KEY_BAG) {
                result.keys.push({
                    pkcs8: bagValue.children[0].full,
                    encrypted: false,
                    friendlyName: attrs.friendlyName,
                    localKeyId: attrs.localKeyId
                });
            } else if (bagId === OID_SHROUDED_KEY_BAG) {
                var epki = bagValue.children[0];
                try {
                    var pkcs8 = await Keys.decryptPbe(epki.children[0], epki.children[1].bytes, password);
                    result.keys.push({
                        pkcs8: pkcs8, encrypted: true,
                        algorithm: Oid.label(epki.children[0].children[0].oid),
                        friendlyName: attrs.friendlyName,
                        localKeyId: attrs.localKeyId
                    });
                } catch (e) {
                    if (String(e.message).indexOf('PBE_UNSUPPORTED') === 0) {
                        result.warnings.push('UNSUPPORTED_KEY_ENCRYPTION:' +
                            Oid.label(epki.children[0].children[0].oid));
                    } else throw e;
                }
            } else {
                result.warnings.push('UNSUPPORTED_BAG:' + Oid.label(bagId));
            }
        }
    }

    function readBagAttributes(attrsNode) {
        var out = { friendlyName: null, localKeyId: null };
        if (!attrsNode || !attrsNode.children) return out;
        attrsNode.children.forEach(function (attr) {
            if (!attr.children || attr.children.length < 2) return;
            var type = attr.children[0].oid;
            var value = attr.children[1].children && attr.children[1].children[0];
            if (!value) return;
            if (type === OID_FRIENDLY_NAME) out.friendlyName = value.text || null;
            else if (type === OID_LOCAL_KEY_ID) out.localKeyId = Asn1.toHex(value.bytes);
        });
        return out;
    }

    /**
     * `encryptedContent` is [0] IMPLICIT OCTET STRING. Most writers emit it
     * primitive, but Java and some HSMs emit it constructed (chunked), in which
     * case the pieces must be concatenated — otherwise decryption sees the DER
     * headers as ciphertext and fails with a bogus "wrong password".
     */
    function findImplicitContent(eci) {
        for (var i = 0; i < eci.children.length; i++) {
            var c = eci.children[i];
            if (c.cls === 2 && c.tagNumber === 0) {
                if (!c.constructed) return c.bytes;
                return Asn1.concat((c.children || []).map(function (k) { return k.bytes; }));
            }
        }
        throw new Error('PFX_NO_ENCRYPTED_CONTENT');
    }

    function unwrapOctets(node) {
        if (!node) throw new Error('PFX_EMPTY_CONTENT');
        if (node.tag === TAG.OCTET_STRING) {
            if (!node.constructed) return node.bytes;
            return Asn1.concat((node.children || []).map(function (k) { return k.bytes; }));
        }
        return node.full;
    }

    async function verifyMac(macData, authSafeBytes, password) {
        var digestInfo = macData.children[0];
        var algOid = digestInfo.children[0].children[0].oid;
        var expected = digestInfo.children[1].bytes;
        var salt = macData.children[1].bytes;
        var iterations = macData.children[2] ? intOf(macData.children[2]) : 1;

        var hashName = algOid === OID_SHA256 ? 'SHA-256' : 'SHA-1';
        var keyLen = hashName === 'SHA-256' ? 32 : 20;

        async function tryPassword(pw) {
            var key = Ciphers.pkcs12Kdf(pw, salt, 3, iterations, keyLen, hashName);
            var ck = await Keys.subtle().importKey('raw', Keys.bufferOf(key),
                { name: 'HMAC', hash: hashName }, false, ['sign']);
            var sig = new Uint8Array(await Keys.subtle().sign('HMAC', ck, Keys.bufferOf(authSafeBytes)));
            return Asn1.bytesEqual(sig, Asn1.u8(expected));
        }

        var ok = await tryPassword(password);
        var out = { ok: ok, algorithm: hashName, iterations: iterations, okWithAlternateEmpty: false };

        // The empty-password ambiguity: "" encodes either as the 2-byte NUL
        // terminator or as nothing at all, depending on the writer. Uint8Array(0)
        // selects the second convention (see Ciphers.bmpWithTerminator).
        if (!ok && (password === '' || password === null || password === undefined)) {
            if (await tryPassword(new Uint8Array(0))) {
                out.okWithAlternateEmpty = true;
                out.alternatePassword = new Uint8Array(0);
            }
        }
        return out;
    }

    function intOf(node) {
        var b = node.bytes, v = 0;
        for (var i = 0; i < b.length; i++) v = v * 256 + b[i];
        return v;
    }

    /**
     * Match each key to its certificate. localKeyId is the intended mechanism,
     * but plenty of bundles omit it, so fall back to comparing the public key
     * derived from the private key against each certificate's SPKI — which is
     * authoritative anyway.
     */
    async function pairKeysAndCerts(result) {
        var X509 = P.X509 || (root && root.PKI && root.PKI.X509);
        for (var i = 0; i < result.keys.length; i++) {
            var key = result.keys[i];
            var parsed = null;
            try { parsed = Keys.parsePrivate(key.pkcs8); } catch (e) { continue; }
            key.parsed = parsed;
            var spki = await Keys.publicFromPrivate(parsed);

            for (var j = 0; j < result.certificates.length; j++) {
                var cert = result.certificates[j];
                if (key.localKeyId && cert.localKeyId && key.localKeyId === cert.localKeyId) {
                    key.certificateIndex = j; cert.keyIndex = i; break;
                }
                if (spki && X509) {
                    try {
                        var c = X509.parse(cert.der);
                        if (Asn1.bytesEqual(c.spki, spki)) { key.certificateIndex = j; cert.keyIndex = i; break; }
                    } catch (e) { /* unreadable cert — leave unpaired */ }
                }
            }
        }
    }

    /* ═══ writing ═══════════════════════════════════════════════════ */

    /**
     * Build a PFX.
     * opts: {certificates:[der], key: pkcs8, password, friendlyName,
     *        profile: 'modern'|'compatible', iterations}
     */
    async function write(opts) {
        var profile = opts.profile === 'compatible' ? 'compatible' : 'modern';
        var iterations = opts.iterations || (profile === 'modern' ? 100000 : 2048);
        var password = opts.password == null ? '' : String(opts.password);
        var certs = (opts.certificates || []).map(Asn1.u8);

        var localKeyId = Keys.randomBytes(20);
        var safeContentsList = [];

        // ── key bag (always shrouded — an unencrypted key in a .pfx is a trap)
        if (opts.key) {
            var shrouded = profile === 'modern'
                ? await Keys.encryptPrivateKey(opts.key, password, iterations)
                : await encryptPkcs12Pbe(opts.key, password, OID_PBE_3DES, iterations);

            var keyBag = Asn1.sequence(
                Asn1.oid(OID_SHROUDED_KEY_BAG),
                Asn1.explicit(0, shrouded),
                bagAttributes(opts.friendlyName, localKeyId)
            );
            safeContentsList.push({ encrypted: false, der: Asn1.sequence(keyBag) });
        }

        // ── cert bags
        if (certs.length) {
            var certBags = certs.map(function (der, idx) {
                var certBag = Asn1.sequence(
                    Asn1.oid(OID_CERT_BAG),
                    Asn1.explicit(0, Asn1.sequence(
                        Asn1.oid(OID_X509_CERT),
                        Asn1.explicit(0, Asn1.octetString(der))
                    )),
                    // Only the leaf gets the localKeyId that ties it to the key;
                    // tagging intermediates too makes Windows pick the wrong one.
                    idx === 0 ? bagAttributes(opts.friendlyName, localKeyId) : bagAttributes(null, null)
                );
                return certBag;
            });
            var certSafe = Asn1.sequence(certBags);
            if (profile === 'modern') {
                safeContentsList.push({
                    encrypted: true,
                    der: await encryptContent(certSafe, password, 'modern', iterations)
                });
            } else {
                safeContentsList.push({
                    encrypted: true,
                    der: await encryptContent(certSafe, password, 'compatible', iterations)
                });
            }
        }

        var authSafe = Asn1.sequence(safeContentsList.map(function (sc) {
            return sc.encrypted
                ? Asn1.sequence(Asn1.oid(OID_ENCRYPTED_DATA), Asn1.explicit(0, sc.der))
                : Asn1.sequence(Asn1.oid(OID_DATA), Asn1.explicit(0, Asn1.octetString(sc.der)));
        }));

        // ── MAC over the authSafe content octets
        var hashName = profile === 'modern' ? 'SHA-256' : 'SHA-1';
        var macSalt = Keys.randomBytes(hashName === 'SHA-256' ? 20 : 8);
        var macKeyLen = hashName === 'SHA-256' ? 32 : 20;
        var macKey = Ciphers.pkcs12Kdf(password, macSalt, 3, iterations, macKeyLen, hashName);
        var hk = await Keys.subtle().importKey('raw', Keys.bufferOf(macKey),
            { name: 'HMAC', hash: hashName }, false, ['sign']);
        var macValue = new Uint8Array(await Keys.subtle().sign('HMAC', hk, Keys.bufferOf(authSafe)));

        return Asn1.sequence(
            Asn1.integerFromNumber(3),
            Asn1.sequence(Asn1.oid(OID_DATA), Asn1.explicit(0, Asn1.octetString(authSafe))),
            Asn1.sequence(
                Asn1.sequence(
                    Asn1.sequence(Asn1.oid(hashName === 'SHA-256' ? OID_SHA256 : OID_SHA1), Asn1.nullValue()),
                    Asn1.octetString(macValue)
                ),
                Asn1.octetString(macSalt),
                Asn1.integerFromNumber(iterations)
            )
        );
    }

    function bagAttributes(friendlyName, localKeyId) {
        var attrs = [];
        if (friendlyName) {
            attrs.push(Asn1.sequence(Asn1.oid(OID_FRIENDLY_NAME), Asn1.set(Asn1.bmpString(friendlyName))));
        }
        if (localKeyId) {
            attrs.push(Asn1.sequence(Asn1.oid(OID_LOCAL_KEY_ID), Asn1.set(Asn1.octetString(localKeyId))));
        }
        return attrs.length ? Asn1.set(attrs) : null;
    }

    async function encryptContent(plainDer, password, profile, iterations) {
        if (profile === 'modern') {
            var salt = Keys.randomBytes(16), iv = Keys.randomBytes(16);
            var key = await Keys.pbkdf2(password, salt, iterations, 32, 'SHA-256');
            var ck = await Keys.subtle().importKey('raw', Keys.bufferOf(key), { name: 'AES-CBC' }, false, ['encrypt']);
            var ct = new Uint8Array(await Keys.subtle().encrypt({ name: 'AES-CBC', iv: Keys.bufferOf(iv) },
                ck, Keys.bufferOf(plainDer)));
            var algId = Asn1.sequence(
                Asn1.oid('1.2.840.113549.1.5.13'),
                Asn1.sequence(
                    Asn1.sequence(Asn1.oid('1.2.840.113549.1.5.12'),
                        Asn1.sequence(Asn1.octetString(salt), Asn1.integerFromNumber(iterations),
                            Asn1.sequence(Asn1.oid('1.2.840.113549.2.9'), Asn1.nullValue()))),
                    Asn1.sequence(Asn1.oid('2.16.840.1.101.3.4.1.42'), Asn1.octetString(iv))
                )
            );
            return encryptedDataWrapper(algId, ct);
        }

        // compatible: PBE-SHA1-RC2-40 for certificates, matching what
        // `openssl pkcs12 -export -legacy` produces and what old stacks expect.
        var salt2 = Keys.randomBytes(8);
        var key2 = Ciphers.pkcs12Kdf(password, salt2, 1, iterations, 5, 'SHA-1');
        var iv2 = Ciphers.pkcs12Kdf(password, salt2, 2, iterations, 8, 'SHA-1');
        var ct2 = Ciphers.rc2Cbc(key2, iv2, Ciphers.padPkcs7(plainDer, 8), false, 40);
        var algId2 = Asn1.sequence(
            Asn1.oid(OID_PBE_RC2_40),
            Asn1.sequence(Asn1.octetString(salt2), Asn1.integerFromNumber(iterations))
        );
        return encryptedDataWrapper(algId2, ct2);
    }

    function encryptedDataWrapper(algId, ciphertext) {
        return Asn1.sequence(
            Asn1.integerFromNumber(0),
            Asn1.sequence(
                Asn1.oid(OID_DATA),
                algId,
                Asn1.context(0, ciphertext, false)   // [0] IMPLICIT OCTET STRING
            )
        );
    }

    async function encryptPkcs12Pbe(pkcs8, password, algOid, iterations) {
        var salt = Keys.randomBytes(8);
        var key = Ciphers.pkcs12Kdf(password, salt, 1, iterations, 24, 'SHA-1');
        var iv = Ciphers.pkcs12Kdf(password, salt, 2, iterations, 8, 'SHA-1');
        var ct = Ciphers.tripleDesCbc(key, iv, Ciphers.padPkcs7(Asn1.u8(pkcs8), 8), false);
        return Asn1.sequence(
            Asn1.sequence(Asn1.oid(algOid), Asn1.sequence(Asn1.octetString(salt), Asn1.integerFromNumber(iterations))),
            Asn1.octetString(ct)
        );
    }

    var API = { read: read, write: write };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) { root.PKI = root.PKI || {}; root.PKI.Pkcs12 = API; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
